import {
  ChangeDetectionStrategy,
  Component,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import {
  Router,
} from '@angular/router';
import {
  OrderApi,
  OrderReply,
} from '@dapi/sdk';
import {
  isOrderCancelAndRefundEligibilityTypeInvoluntary,
} from '@dapi/sdk/helpers';
import {
  OrderModel,
  OrderStore,
  retrieveOrderEntityFromApi,
  selectCurrentOrder,
} from '@des/store/order';
import {
  OrderEligibilitiesStore,
  selectOrderEligibilitiesState,
} from '@des/store/order-eligibilities';
import {
  select,
  Store,
} from '@ngrx/store';
import {
  EventTrackService,
} from '@o3r/analytics';
import {
  ApiFactoryService,
} from '@o3r/apis-manager';
import {
  C11nService,
} from '@o3r/components';
import {
  ConfigurationBaseService,
  ConfigurationObserver,
  DynamicConfigurable,
  O3rConfig,
} from '@o3r/configuration';
import {
  computeItemIdentifier,
  Context,
  O3rComponent,
} from '@o3r/core';
import {
  Localization,
} from '@o3r/localization';
import {
  LinkableToRuleset,
  RulesEngineRunnerService,
} from '@o3r/rules-engine';
import {
  AnalyticsService,
} from '@refx/analytics-core';
import {
  BackToMmbPresComponent,
  BackToMmbPresConfig,
  BackToMmbPresContextInput,
  BackToMmbPresContextOutput,
} from '@refx/servicing-components';
import {
  getOrderMainLastName,
  REFX_COMPONENTS_COMMON_RUNTIME_CONFIG_ID,
  RefxComponentsCommonRuntimeConfig,
} from '@refx/shared-common';
import {
  UiMessageService,
} from '@refx/shared-services';
import {
  SetIsRefundInitiatedFromSelfReacc,
  UiStatusStore,
} from '@refx/shared-store';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
} from 'rxjs';
import {
  filter,
  map,
  skipWhile,
  take,
} from 'rxjs/operators';
import {
  COMPONENTS_CONFIGURATION_NAME,
} from '../../../configuration/index';
import {
  AppRoutes,
  RefundRoutes,
  SelfReaccommodationRoutes,
  sharedPageIds,
  sharedPageNames,
} from '../../../configuration/routes';
import {
  isEligibleToRefund,
} from '../../../helpers/index';
import {
  PreviousUrlService,
} from '../../../services/index';
import {
  analyticsEvents,
  RecoveryComponentAnalytics,
} from './recovery.analytics';
import {
  RECOVERY_CONFIG_ID,
  RECOVERY_DEFAULT_CONFIG,
  RecoveryConfig,
} from './recovery.config';
import {
  RecoveryTranslation,
  translations,
} from './recovery.translation';

@O3rComponent({
  componentType: 'Page'
})
@Component({
  selector: 'refx-servicing-recovery',
  templateUrl: './recovery.template.html',
  styleUrls: ['./recovery.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class RecoveryComponent implements OnChanges, OnDestroy, OnInit, DynamicConfigurable<RecoveryConfig>, LinkableToRuleset {
  private readonly componentName = computeItemIdentifier('RecoveryComponent', COMPONENTS_CONFIGURATION_NAME);

  /** Input configuration to override the default configuration of the component */
  @Input()
  public config: Partial<RecoveryConfig> | undefined;

  /** Dynamic configuration based on the input override configuration and the configuration service if used by the application */
  @O3rConfig()
  private readonly dynamicConfig$: ConfigurationObserver<any>;

  /** Configuration stream based on the input and the stored configuration */
  public config$: Observable<RecoveryConfig>;

  /** Deeplink config error */
  public deeplinkConfigError?: boolean;

  /** Deeplink forbidden dapi error */
  public deeplinkDapiForbiddenError?: boolean;

  /**
   * Flag for refund flow
   */
  private isRefundFlow = false;

  /**
   * Flag for self reacc flow
   */
  private isSelfReaccFlow = false;

  /**
   * Flag for back navigation tracking
   */
  private readonly hasBackNavigationFromRefundReview: boolean;

  /**
   * Observable of whether to show or not the refund teaser in recovery page
   */
  public checkOrderEligibilityForRefundTeaser$: Observable<boolean | undefined>;

  /**
   * The order's record locator;
   */
  public recLoc$: Observable<string>;

  /**
   * The last name associated with the PNR.
   */
  public lastName$: Observable<string>;

  /** Component retrieved from the configuration's component key */
  public backToMmbComponent$: Observable<Context<BackToMmbPresContextInput, BackToMmbPresContextOutput> & DynamicConfigurable<BackToMmbPresConfig>>;

  /**
   * Global configuration stream
   */
  protected globalConfig$: Observable<RefxComponentsCommonRuntimeConfig>;

  /**
   * Subject to indicate if the itinerary exists in the order
   */
  private readonly noItineraryExistInOrderSubject$: BehaviorSubject<boolean | undefined> = new BehaviorSubject(false);

  /**
   * Observable to indicate if the itinerary exists in the order
   */
  public noItineraryExistInOrder$ = this.noItineraryExistInOrderSubject$.asObservable();

  /**
   *  No itinerary warning code
   */
  protected readonly NO_ITINERARY_WARNING_CODE = '64938';

  /**
   * Localization of the component
   */
  @Input()
  @Localization('./recovery.localization.json')
  public translations: RecoveryTranslation;

  /** Analytics events for the recovery page */
  public readonly analyticsEvents: RecoveryComponentAnalytics = analyticsEvents;

  /**
   * Flag to indicate if rebooking is in progress
   */
  public rebookingInProgress = false;

  constructor(
    public uiMessageService: UiMessageService,
    private readonly eventTrackService: EventTrackService,
    private readonly router: Router,
    private readonly analyticsService: AnalyticsService,
    private readonly previousUrlService: PreviousUrlService,
    private readonly store: Store<OrderStore & OrderEligibilitiesStore & UiStatusStore>,
    private readonly c11nService: C11nService,
    private readonly ngZone: NgZone,
    private readonly apiFactoryService: ApiFactoryService,
    configurationService: ConfigurationBaseService,
    @Optional() private readonly rulesEngineService?: RulesEngineRunnerService
  ) {
    this.dynamicConfig$ = new ConfigurationObserver<any>(RECOVERY_CONFIG_ID, RECOVERY_DEFAULT_CONFIG, configurationService);
    this.config$ = this.dynamicConfig$.asObservable();
    this.globalConfig$ = configurationService.getConfig(REFX_COMPONENTS_COMMON_RUNTIME_CONFIG_ID);
    this.translations = translations;
    const navigationState = this.router.getCurrentNavigation()?.extras?.state;
    this.deeplinkConfigError = navigationState?.deeplinkConfigError;
    this.deeplinkDapiForbiddenError = navigationState?.deeplinkDapiForbiddenError;
    this.rebookingInProgress = !!navigationState?.rebookingInProgress;
    // Track back navigation from refund review page if coming from self reacc flow
    const currentNavigation = router.getCurrentNavigation();
    this.hasBackNavigationFromRefundReview = !!(currentNavigation
      && currentNavigation.trigger === 'popstate'
      && currentNavigation.previousNavigation
      && currentNavigation.previousNavigation.extractedUrl.toString().includes(AppRoutes.refund));
    const currentOrder$ = this.store.pipe(
      select(selectCurrentOrder),
      filter<OrderModel>((order) => !!order)
    );
    this.checkOrderEligibilityForRefundTeaser$ = combineLatest([
      currentOrder$,
      this.store.pipe(
        select(selectOrderEligibilitiesState),
        skipWhile((eligibilitiesState) => !!eligibilitiesState.isPending)
      )
    ]).pipe(
      map(([currentOrder, eligibilitiesState]) => {
        const eligibilityInformation = currentOrder && eligibilitiesState.entities[currentOrder.id];
        return (
          (this.isSelfReaccFlow || this.hasBackNavigationFromRefundReview)
          && !!eligibilityInformation
          && isOrderCancelAndRefundEligibilityTypeInvoluntary(eligibilityInformation)
          && isEligibleToRefund(currentOrder, eligibilityInformation)
        );
      })
    );
    this.backToMmbComponent$ = this.config$.pipe(this.c11nService.getPresenter(BackToMmbPresComponent as any, 'backToMmbCustomComponentKey') as any);
    this.recLoc$ = currentOrder$.pipe(map((order) => order.id));
    this.lastName$ = currentOrder$.pipe(map((order) => getOrderMainLastName(order)));
  }

  private checkUrlFlow(url: string, appRoute: AppRoutes): boolean {
    const regex = new RegExp(`^/${appRoute}`);
    return regex.test(url);
  }

  public ngOnChanges(change: SimpleChanges) {
    if (change.config) {
      this.dynamicConfig$.next(this.config);
    }
  }

  public ngOnInit() {
    this.analyticsService.triggerPageView(sharedPageIds.recovery, sharedPageNames.recovery);
    this.uiMessageService.uiErrors$.pipe(
      take(1)
    ).subscribe((errors) => {
      if (errors.length > 0) {
        errors.forEach((error) => {
          this.eventTrackService.addCustomEvent({
            context: new this.analyticsEvents.recoveryError({
              errorName: error.titleKey.key
            })
          });
        });
      } else {
        this.eventTrackService.addCustomEvent({
          context: new this.analyticsEvents.recoveryError({
            errorName: translations.defaultError
          })
        });
      }
    });
    this.previousUrlService.previousUrl$.pipe(take(1)).subscribe((url) => {
      this.isRefundFlow = this.checkUrlFlow(url, AppRoutes.refund);
      this.isSelfReaccFlow = this.checkUrlFlow(url, AppRoutes.selfReaccommodation);
      if (this.isRefundFlow) {
        combineLatest([
          this.recLoc$, this.lastName$
        ]).pipe(take(1)).subscribe(async ([recLoc, lastName]) => {
          try {
            const orderReply = await this.dispatchRetrieveOrderWithEligibilities(recLoc, lastName);
            this.noItineraryExistInOrderSubject$.next(orderReply.warnings?.some((warning) => warning.code === this.NO_ITINERARY_WARNING_CODE));
          } catch {}
        });
      }
    });

    if (this.rulesEngineService) {
      this.rulesEngineService.enableRuleSetFor(this.componentName);
    }
  }

  /**
   * Function to dispatch an action to the OrderStore in order to retrieve an order, with eligibilities.
   *
   * @param orderId The order id
   * @param lastName The last name associated with the order
   *
   * @returns the order reply
   */
  private async dispatchRetrieveOrderWithEligibilities(orderId: string, lastName: string): Promise<OrderReply> {
    const orderApi = this.apiFactoryService.getApi(OrderApi);
    const call = orderApi.retrieveOrder({
      orderId,
      lastName,
      showOrderEligibilities: true
    });
    this.store.dispatch(retrieveOrderEntityFromApi({
      call,
      id: orderId
    }));
    return await call;
  }

  public navigate(url?: string) {
    if (this.isRefundFlow) {
      this.ngZone.run(() => window.open(url, '_self'));
    } else {
      this.ngZone.run(() => url && url !== '_blank'
        ? window.open(url, '_self')
        : this.router.navigate([AppRoutes.selfReaccommodation, SelfReaccommodationRoutes.retrieve]));
    }
    // Prevent default browser behaviour for the hyperlink
    return false;
  }

  /**
   * Navigate to the refund flow
   */
  public async goToRefund() {
    const isPartialRefundEnabled = await firstValueFrom(this.globalConfig$.pipe(
      map((globalConfig) => globalConfig.globalSelectPassengersForRefund)
    ));
    const isMultiPaxOrder = await firstValueFrom(
      this.store
        .pipe(
          select(selectCurrentOrder),
          filter<OrderModel>((order) => !!order),
          map((order) => order.travelers?.filter((traveler) => traveler.passengerTypeCode !== 'INF')),
          map((travelers) => !!travelers?.length && travelers?.length > 1)
        )
    );
    const redirectUrl = [AppRoutes.refund, isPartialRefundEnabled && isMultiPaxOrder ? RefundRoutes.selectPassengers : RefundRoutes.review];
    this.store.dispatch(SetIsRefundInitiatedFromSelfReacc({
      isRefundInitiatedFromSelfReacc: true
    }));
    this.ngZone.run(() => this.router.navigate(redirectUrl));
  }

  public ngOnDestroy() {
    if (this.rulesEngineService) {
      this.rulesEngineService.disableRuleSetFor(this.componentName);
    }
  }
}
